import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Calendar, User, ArrowLeft, Tag } from "lucide-react";

const BlogPostPage = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const res = await publicApi.getBlogPost(slug);
                setPost(res.data);
            } catch (err) {
                console.error("Failed to load post:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPost();
    }, [slug]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    };

    if (loading) {
        return (
            <div className="container mx-auto px-6 lg:px-12 py-16">
                <div className="max-w-3xl mx-auto animate-pulse">
                    <div className="h-8 bg-secondary rounded w-2/3 mb-4" />
                    <div className="h-4 bg-secondary rounded w-1/3 mb-8" />
                    <div className="h-64 bg-secondary rounded mb-8" />
                    <div className="space-y-3">
                        {[1,2,3,4,5].map(i => <div key={i} className="h-4 bg-secondary rounded" />)}
                    </div>
                </div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="container mx-auto px-6 lg:px-12 py-16 text-center">
                <h1 className="font-heading font-bold text-2xl mb-4">Post Not Found</h1>
                <Link to="/blog" className="text-primary hover:underline">Back to Blog</Link>
            </div>
        );
    }

    return (
        <div data-testid="blog-post-page">
            {/* SEO meta via Helmet would go here */}
            <article className="container mx-auto px-6 lg:px-12 py-12">
                <div className="max-w-3xl mx-auto">
                    {/* Back link */}
                    <Link to="/blog" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 font-mono text-sm uppercase tracking-wider" data-testid="blog-back-link">
                        <ArrowLeft className="h-4 w-4" /> Back to Blog
                    </Link>

                    {/* Title */}
                    <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
                        {post.title}
                    </h1>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
                        <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(post.published_at)}
                        </span>
                        <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {post.author}
                        </span>
                    </div>

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-8">
                            {post.tags.map(tag => (
                                <Link key={tag} to={`/blog?tag=${tag}`}>
                                    <Badge variant="outline" className="rounded-none">
                                        <Tag className="h-3 w-3 mr-1" /> {tag}
                                    </Badge>
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* Featured image */}
                    {post.featured_image && (
                        <div className="mb-10 border border-border">
                            <img
                                src={resolveImageUrl(post.featured_image)}
                                alt={post.title}
                                className="w-full max-h-[500px] object-cover"
                            />
                        </div>
                    )}

                    {/* Content */}
                    <div
                        className="prose prose-invert prose-orange max-w-none
                            prose-headings:font-heading prose-headings:tracking-tight
                            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                            prose-img:rounded-none prose-img:border prose-img:border-border
                            prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground
                            prose-strong:text-foreground
                            prose-p:text-muted-foreground prose-li:text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                        data-testid="blog-post-content"
                    />

                    {/* Bottom CTA */}
                    <div className="border-t border-border mt-16 pt-8">
                        <div className="bg-card border border-border p-8">
                            <h3 className="font-heading font-bold text-xl mb-2">Need Expert Guidance?</h3>
                            <p className="text-muted-foreground mb-4">Our team is ready to help you find the right dry ice blasting solution.</p>
                            <div className="flex gap-4">
                                <Link to="/guidance">
                                    <button className="bg-primary text-primary-foreground px-6 py-2 font-mono text-sm uppercase tracking-wider hover:bg-primary/90 transition-colors" data-testid="blog-cta-guidance">
                                        Get Guidance
                                    </button>
                                </Link>
                                <Link to="/products">
                                    <button className="border border-border px-6 py-2 font-mono text-sm uppercase tracking-wider hover:border-primary transition-colors" data-testid="blog-cta-products">
                                        View Equipment
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </article>
        </div>
    );
};

export default BlogPostPage;
